import React, { useState, useEffect } from 'react';
import {
  Button, Col, Row, Spin,
  Table, Form,
  Typography,
  Select,
  notification,
  Input,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import './ReportIncome.scss';
import { FilePdfOutlined } from '@ant-design/icons';
import FilterIcon from 'mdi-react/FilterIcon';
import {
  get, isSuperAgentError, patch, RequestWrapper, ResponseWrapper,
} from '../../dataSource/requests';
import { downloadPdf } from '../../utils/helpers';
import {
  formatCPF,
} from '../../utils/format';

interface TablePagination {
  current: number | undefined,
  defaultCurrent: number | undefined,
  pageSize: number | undefined,
  showQuickJumper: boolean | {
    goButton?: React.ReactNode;
  },
  total: number | undefined,
  itemsInPage?: number | undefined,
}
interface IReportIncome {
  bpMatriculation: string,
  companyName: string,
  cpfCnpj: string,
  customerName: string,
  idInforme: string,
  irTotalValue: string,
  matriculation: string,
  partner: string,
  year: string
}

interface IBaseYear {
  baseYear: string,
  id: number,
  status: string
}

const ReportIncome = (): JSX.Element => {
  const navigate = useNavigate();

  const [formInstance] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [tableDataSource, setTableDataSource] = useState<Array<IReportIncome>>([]);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tablePagination, setTablePagination] = useState<TablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 15,
    showQuickJumper: true,
    total: 0,
  });
  const [baseYearOptions, setBaseYearOprions] = useState<IBaseYear[]>([]);

  const getBaseYear = async ():Promise<void> => {
    let response: ResponseWrapper | undefined;

    const requestReference: RequestWrapper = get({
      url: '/ir-base-year',
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);
    }

    switch (true) {
      case (response != null && response?.body?.code === 22000):
        setBaseYearOprions(response?.body?.incomingReportOutputs);
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar os lista de exercícios/ano-base, por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  const dowloadPdf = async (pdfIdentity:number):Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;

    setLoading(true);

    const requestReference: RequestWrapper = get({
      url: `/income-tax/download-income-tax-pdf/${pdfIdentity}`,
      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      }
    }

    switch (true) {
      case (response != null && response?.body?.code === 22005):
        downloadPdf(response?.body?.incomingTaxBase64, `informe-rendimentos-${pdfIdentity}`);
        break;

      case (responseError?.body?.code === -24003): {
        notification.error({
          message: 'Erro',
          description: 'O pdf do imposto de renda não foi encontrado.',
        });
        break;
      }

      case (responseError?.body?.code === -24004): {
        notification.error({
          message: 'Erro',
          description: 'Você não tem permissão para baixar esse arquivo.',
        });
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro. Por favor tente novamente mais tarde.',
        });
        break;
    }

    setLoading(false);
  };

  const executeFilter = async (args: {
    page?: number,
    pageSize?: number,
    orderBy?: string,
    orderByDir?: string,
  } = {
    page: 0,
    pageSize: 15,
    orderBy: 'companyName',
    orderByDir: 'DESC',
  }): Promise<void> => {
    setTableLoading(true);
    let response: ResponseWrapper | undefined;

    const values = formInstance.getFieldsValue();

    const argsQuery = {
      orderBy: ('orderBy' in args) ? args.orderBy : 'companyName',
      page: ('page' in args && args.page >= 1) ? args?.page : 0,
      pageSize: ('pageSize' in args) ? args.pageSize : 15,
      sort: ('orderByDir' in args) ? args.orderByDir : 'DESC',
    };

    const requestReference: RequestWrapper = patch({
      url: `/income-tax/listAll/${values.baseYear}`,
      params: {
        ...Object.fromEntries(
          Object
            .entries(values)
            .filter(
              ([, elementValue]) => {
                return elementValue !== null && elementValue !== values.baseYear;
              },
            ),
        ),
      },
      query: {
        ...Object.fromEntries(
          Object
            .entries(argsQuery)
            .filter(
              ([, elementValue]) => {
                return elementValue != null;
              },
            ),
        ),
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log('error', error);
    }

    switch (true) {
      case (response != null && response?.body?.code === 22004): {
        const pagination = tablePagination;
        pagination.pageSize = response?.body?.paginationInfo?.maxSize;
        pagination.current = response?.body?.paginationInfo?.pageNumber + 1;
        pagination.total = response?.body?.paginationInfo?.totalElements;

        if (response?.body?.incomingReportOutputs.length > 0) {
          setTableDataSource(response?.body?.incomingReportOutputs);
        } else {
          setTableDataSource([]);
          notification.info({
            message: 'Atenção',
            description: 'Desculpe, não encontramos IR para esse exercício/ano-base',
          });
        }

        setTablePagination((previous) => {
          return ({
            ...previous,
            current: pagination.current,
            defaultCurrent: pagination.defaultCurrent,
            pageSize: pagination.pageSize,
            showQuickJumper: pagination.showQuickJumper,
            total: pagination.total,
          });
        });
      } break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao pesquisar os IR, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableLoading(false);
  };

  const handleTableChange = (pagination: any, _filters: any, sorter: any): void => {
    setTableLoading(true);

    setTablePagination((previous) => {
      return ({
        ...previous,
        current: pagination.current || previous.current,
        defaultCurrent: pagination.defaultCurrent || previous.defaultCurrent,
        pageSize: pagination.pageSize || previous.pageSize,
        showQuickJumper: pagination.showQuickJumper
          || previous.showQuickJumper,
        total: pagination.total || previous.total,
      });
    });

    let finalOrderDir;

    if (sorter?.field && sorter?.order) {
      switch (true) {
        case (sorter.order === 'descend'):
          finalOrderDir = 'DESC';
          break;

        case (sorter.order === 'ascend'):
        case (sorter.order == null):
        default:
          finalOrderDir = 'ASC';
          break;
      }
    }

    executeFilter({
      page: (pagination?.current)
        ? pagination?.current - 1
        : 0,
      orderBy: sorter?.field || 'companyName',
      orderByDir: finalOrderDir || 'DESC',
      pageSize: pagination.pageSize,
    });
  };

  useEffect(() => {
    getBaseYear();

    return () => {
      setLoading(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="ReportIncome">
      <Spin
        tip="Carregando..."
        spinning={loading}
      >
        <Row gutter={[24, 24]}>
          <Col xs={{ span: 24 }}>
            <Row
              gutter={[24, 8]}
              className="title-container"
            >
              <Col>
                <span className="page-title">
                  Informe de Rendimentos
                </span>
              </Col>
              <Col>
                <Typography.Title level={5}>
                  O Informe de Rendimento com as distribuições de sobras referente ao exercício anterior somente estará disponível após a Assembleia Geral Ordinária da Coopercitrus.
                </Typography.Title>
              </Col>
            </Row>
          </Col>
        </Row>
        <Form
          scrollToFirstError
          noValidate
          layout="vertical"
          form={formInstance}
          onFinish={
          async (): Promise<void> => {
            await executeFilter();
          }
        }
        >
          <Row
            gutter={24}
            className="filters-container"
          >
            <Col xs={{ span: 24 }} lg={{ span: 18 }}>
              <Row gutter={[8, 24]} className="inputsForm">
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Exercício/ano-base"
                    name="baseYear"
                    rules={[{ required: true, message: 'Selecione um exercício/ano-base' }]}
                  >
                    <Select
                      showSearch
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Selecione o ano base"
                    >
                      {baseYearOptions.length > 0
                       && baseYearOptions.map(({ baseYear, id }:IBaseYear) => (
                         <Select.Option key={id} value={baseYear}>
                           {baseYear}
                         </Select.Option>
                       ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Empresa emissora"
                    name="companyName"
                    rules={[]}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Digite o nome da empresa emissora"
                    />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Número da matrícula"
                    name="matriculation"
                    rules={[]}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Digite o número da matrícula"
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[8, 24]} className="inputsForm">
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Nome do Cooperado"
                    name="customerName"
                    rules={[]}
                  >
                    <Input
                      style={{ width: '100%' }}
                      placeholder="Digite o nome do Cooperado"
                    />
                  </Form.Item>
                </Col>
                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="CPF/CNPJ"
                    name="cpfCnpj"
                    rules={[
                      {
                        required: false,
                        message: 'Por favor, insira seu CPF/CNPJ e utilize somente números.',
                      },
                      {
                        max: 14,
                        message: 'O campo deve ter no máximo até 14 dígitos.',
                      },
                      {
                        pattern: /^[a-z,A-z,0-9,@,.,_,-]+$/,
                        message: 'Não utilize caracteres especiais ou acentuados.',
                      },
                      {
                        validator: (_rule, value): Promise<void | Error> => {
                          if (value) {
                            const cleanValue = value.replace(/[^\w\.,-]|\d/g, '');

                            if (cleanValue.length > 0) {
                              return Promise.reject(new Error('Utilize apenas números sem pontuação.'));
                            }

                            return Promise.resolve();
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      maxLength={14}
                      min={0}
                      max={14}
                      autoComplete="cpfCnpj"
                      style={{ width: '100%' }}
                      placeholder="Digite o CPF/CNPJ"
                    />
                  </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} lg={{ span: 8 }}>
                  <Form.Item
                    label="Valor Total do IR"
                    name="irTotalValue"
                    rules={[]}
                  >
                    <Input
                      type="number"
                      style={{ width: '100%' }}
                      placeholder="Digite o valor total do IR"
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                <Form.Item
                  label=" "
                >
                  <Button
                    className="btn-filter"
                    type="primary"
                    htmlType="submit"
                  >
                    <FilterIcon />
                    Filtrar
                  </Button>
                </Form.Item>
              </Col>
            </Col>
          </Row>
        </Form>
        {tableDataSource.length > 0 && (
          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    className="AccountDeletionRequests__Table"
                    columns={[
                      {
                        title: <div className="table-titles">Nome da Empresa Emissora</div>,
                        key: 'companyName',
                        dataIndex: 'companyName',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 250,
                      },
                      {
                        title: <div className="table-titles">Nome do Cooperado</div>,
                        key: 'customerName',
                        dataIndex: 'customerName',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 300,
                      },
                      {
                        title: <div className="table-titles">CPF/CNPJ</div>,
                        key: 'cpfCnpj',
                        dataIndex: 'cpfCnpj',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 200,
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => {
                          if (record.cpfCnpj) {
                            return formatCPF(record.cpfCnpj);
                          }
                          return '';
                        },
                      },
                      {
                        title: <div className="table-titles">Ano-base</div>,
                        key: 'year',
                        dataIndex: 'year',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 110,
                      },
                      {
                        title: <div className="table-titles">Número da Matrícula</div>,
                        key: 'matriculation',
                        dataIndex: 'matriculation',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 200,
                      },
                      {
                        title: <div className="table-titles">Valor Total do IR</div>,
                        key: 'irTotalValue',
                        dataIndex: 'irTotalValue',
                        ellipsis: true,
                        sorter: true,
                        defaultSortOrder: 'ascend',
                        width: 200,
                      },
                      {
                        title: <div className="table-titles">Download</div>,
                        dataIndex: 'idInforme',
                        key: 'acoes',
                        render: (
                          _value: string | number,
                          record: any,
                        ): React.ReactNode => (
                          <span>
                            <Button
                              icon={<FilePdfOutlined />}
                              onClick={
                                async (): Promise<void> => {
                                  await dowloadPdf(record.idInforme);
                                }
                              }
                            />
                          </span>

                        ),
                        width: 110,
                      },
                    ]}
                    dataSource={tableDataSource}
                    loading={tableLoading}
                    rowKey={(record): string => record.id as unknown as string}
                    pagination={{
                      defaultCurrent: tablePagination.defaultCurrent || 1,
                      current: tablePagination.current || 1,
                      pageSize: tablePagination.pageSize || 15,
                      total: tablePagination.total || 0,
                      showQuickJumper: tablePagination.showQuickJumper || true,
                      pageSizeOptions: ['1', '15', '50', '100'],
                      showSizeChanger: true,
                    }}
                    onChange={handleTableChange}
                    scroll={{
                      x: '100vw',
                    }}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        )}
      </Spin>
    </div>
  );
};

export default ReportIncome;
